import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { Subscription } from "./api/subscription/subscription.type";
import { User, UserType } from "./api/user/user.type";

type DateLike =
  | number
  | Date
  | { toMillis?: () => number; seconds?: number }
  | null
  | undefined;

export const dateLikeToMillis = (value: DateLike): number | null => {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  return null;
};

export const isTrialExpired = (
  subscription?: Partial<Subscription> | null,
  now = Date.now(),
) => {
  if (!subscription?.trialStartDate && !subscription?.trialEndDate) return false;
  const trialEnd = dateLikeToMillis(
    subscription.trialEndDate || subscription.endDate,
  );

  if (subscription.status === "trialing") {
    return trialEnd !== null && trialEnd <= now;
  }

  return subscription.status === "denied" && !subscription.stripeSubscriptionId;
};

export const isSubscriptionAccessBlocked = (
  subscription?: Partial<Subscription> | null,
) =>
  !subscription ||
  subscription.status === "denied" ||
  subscription.status === "cancelled" ||
  isTrialExpired(subscription);

export type SubscriptionOwner = {
  ownerId: string;
  ownerType?: UserType;
};

const resolveOwnerCandidate = async (
  ownerId?: string,
): Promise<SubscriptionOwner | null> => {
  if (!ownerId) return null;
  const ownerSnap = await getDoc(doc(db, "users", ownerId));
  if (!ownerSnap.exists()) return { ownerId };

  const owner = ownerSnap.data() as User;
  if (owner.userType === UserType.teacher && owner.createdBy) {
    const schoolSnap = await getDoc(doc(db, "users", owner.createdBy));
    return {
      ownerId: owner.createdBy,
      ownerType: schoolSnap.exists()
        ? (schoolSnap.data() as User).userType
        : UserType.school,
    };
  }

  return { ownerId, ownerType: owner.userType };
};

export const resolveSubscriptionOwner = async (
  user: User,
): Promise<SubscriptionOwner> => {
  if (
    user.userType === UserType.school ||
    user.userType === UserType.independentTeacher
  ) {
    return { ownerId: user.uid, ownerType: user.userType };
  }

  if (user.userType === UserType.teacher) {
    return (
      (await resolveOwnerCandidate(user.createdBy)) || {
        ownerId: user.uid,
        ownerType: user.userType,
      }
    );
  }

  if (user.userType === UserType.student) {
    return (
      (await resolveOwnerCandidate(user.teacherId || user.createdBy)) || {
        ownerId: user.uid,
        ownerType: user.userType,
      }
    );
  }

  if (user.userType === UserType.parent) {
    const directOwner = await resolveOwnerCandidate(user.createdBy);
    if (
      directOwner?.ownerType === UserType.school ||
      directOwner?.ownerType === UserType.independentTeacher
    ) {
      return directOwner;
    }

    const childSnapshot = await getDocs(
      query(
        collection(db, "users"),
        where("parentId", "==", user.uid),
        where("userType", "==", UserType.student),
        limit(1),
      ),
    );
    const child = childSnapshot.docs[0]?.data() as User | undefined;
    if (child) return resolveSubscriptionOwner(child);
    if (directOwner) return directOwner;
  }

  return { ownerId: user.uid, ownerType: user.userType };
};

export const getLatestSubscription = async (ownerId: string) => {
  const snapshot = await getDocs(
    query(
      collection(db, "subscriptions"),
      where("userId", "==", ownerId),
      orderBy("endDate", "desc"),
      limit(1),
    ),
  );

  if (snapshot.empty) return null;
  return {
    id: snapshot.docs[0].id,
    ...snapshot.docs[0].data(),
  } as Subscription;
};

export const isAccountAttachedToSchool = (
  user: User,
  owner: SubscriptionOwner,
) =>
  owner.ownerType === UserType.school &&
  [UserType.teacher, UserType.student, UserType.parent].includes(user.userType);
